import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Coins, Check, ArrowLeft, Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuyTokens = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadData();
    
    // Verificar se voltou do Stripe
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [clientId, searchParams]);

  const loadData = async () => {
    try {
      const [packagesRes, balanceRes] = await Promise.all([
        axios.get(`${API}/admin/packages`),
        axios.get(`${API}/admin/balance/${clientId}`)
      ]);
      
      setPackages(packagesRes.data);
      setBalance(balanceRes.data);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar pacotes");
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 10;
    
    if (attempt >= maxAttempts) {
      toast.error("Tempo esgotado para verificar pagamento. Verifique seu saldo.");
      setCheckingPayment(false);
      return;
    }
    
    setCheckingPayment(true);
    
    try {
      const res = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      
      if (res.data.payment_status === 'paid') {
        if (res.data.tokens_credited) {
          toast.success(`Pagamento confirmado! ${res.data.tokens} tokens adicionados.`);
          loadData(); // Recarregar saldo
          setCheckingPayment(false);
          
          // Limpar URL
          navigate(`/buy-tokens/${clientId}`, { replace: true });
          return;
        }
      } else if (res.data.status === 'expired') {
        toast.error("Sessão de pagamento expirada.");
        setCheckingPayment(false);
        return;
      }
      
      // Continuar polling
      setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), 2000);
      
    } catch (error) {
      console.error("Erro ao verificar pagamento:", error);
      toast.error("Erro ao verificar status do pagamento");
      setCheckingPayment(false);
    }
  };

  const handlePurchase = async (packageData) => {
    setPurchasing(true);
    
    try {
      const originUrl = window.location.origin;
      
      // Mapear nome do pacote para ID
      const packageIdMap = {
        "Básico": "basico",
        "Pro": "pro",
        "Premium": "premium",
        "Enterprise": "enterprise"
      };
      
      const packageId = packageIdMap[packageData.name];
      
      if (!packageId) {
        toast.error("Pacote inválido");
        return;
      }
      
      const res = await axios.post(
        `${API}/payments/checkout/session?package_id=${packageId}&origin_url=${encodeURIComponent(originUrl)}&client_id=${clientId}`
      );
      
      // Redirecionar para Stripe
      if (res.data.url) {
        window.location.href = res.data.url;
      }
      
    } catch (error) {
      console.error("Erro ao iniciar pagamento:", error);
      toast.error("Erro ao processar pagamento");
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-900/10 via-transparent to-transparent"></div>
      
      <div className="relative z-10 container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white"
            data-testid="btn-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          
          <div className="flex items-center space-x-3">
            <Coins className="w-8 h-8 text-red-600" />
            <span className="text-2xl font-bold">COMPRAR TOKENS</span>
          </div>
          
          <div className="w-20"></div>
        </div>

        {/* Checking Payment */}
        {checkingPayment && (
          <Card className="bg-blue-900/20 border-blue-600/30 mb-8">
            <CardContent className="py-6">
              <div className="flex items-center justify-center space-x-3">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                <span className="text-lg">Verificando pagamento...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Balance */}
        <Card className="bg-[#1a1a1a] border-gray-800 mb-8" data-testid="current-balance">
          <CardHeader>
            <CardTitle>Seu Saldo Atual</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-4xl font-bold text-red-500">{balance?.tokens || 0}</div>
                <p className="text-sm text-gray-400 mt-1">análises disponíveis</p>
              </div>
              <div className="text-right text-sm text-gray-500">
                <p>Total comprado: {balance?.tokens_purchased || 0}</p>
                <p>Total usado: {balance?.tokens_used || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Packages */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">Escolha seu pacote</h2>
          <p className="text-gray-400">Pagamento seguro via Stripe</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg) => {
            const pricePerToken = pkg.price_brl / pkg.tokens;
            
            return (
              <Card 
                key={pkg.id}
                className={`${
                  pkg.popular 
                    ? 'bg-gradient-to-br from-red-900/20 to-red-600/10 border-red-600/50 relative' 
                    : 'bg-[#1a1a1a] border-gray-800'
                }`}
                data-testid={`package-${pkg.name.toLowerCase()}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-red-600 text-white">Mais Popular</Badge>
                  </div>
                )}
                
                <CardHeader>
                  <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                  <CardDescription>{pkg.description}</CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-4xl font-bold text-red-500">
                      R$ {pkg.price_brl.toFixed(2)}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      {pkg.tokens} análises
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      R$ {pricePerToken.toFixed(2)} por análise
                    </p>
                  </div>

                  <div className="space-y-2 pt-4 border-t border-gray-800">
                    <div className="flex items-center text-sm text-gray-300">
                      <Check className="w-4 h-4 text-green-500 mr-2" />
                      Análise com IA
                    </div>
                    <div className="flex items-center text-sm text-gray-300">
                      <Check className="w-4 h-4 text-green-500 mr-2" />
                      Locais recomendados
                    </div>
                    <div className="flex items-center text-sm text-gray-300">
                      <Check className="w-4 h-4 text-green-500 mr-2" />
                      Criativos gerados
                    </div>
                  </div>

                  <Button
                    className={`w-full ${
                      pkg.popular 
                        ? 'bg-red-600 hover:bg-red-700' 
                        : 'bg-gray-800 hover:bg-gray-700'
                    }`}
                    onClick={() => handlePurchase(pkg)}
                    disabled={purchasing || checkingPayment}
                    data-testid={`btn-buy-${pkg.name.toLowerCase()}`}
                  >
                    {purchasing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Comprar Agora
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Box */}
        <Card className="bg-blue-900/10 border-blue-600/30 mt-8">
          <CardContent className="py-6">
            <div className="flex items-start space-x-3">
              <CreditCard className="w-5 h-5 text-blue-500 mt-0.5" />
              <div className="text-sm text-gray-300">
                <p className="font-semibold mb-2">Pagamento 100% Seguro</p>
                <ul className="space-y-1 text-gray-400">
                  <li>• Processado via Stripe (líder mundial em pagamentos)</li>
                  <li>• Seus dados de cartão nunca passam por nossos servidores</li>
                  <li>• Aceita PIX e cartões de crédito</li>
                  <li>• Tokens creditados automaticamente após confirmação</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BuyTokens;
